import './cliente-form-modal.js';
import type { Cliente, ClassificacaoCliente } from '@pgcustom/shared-types';
import { formatarCpfExibicao } from '@pgcustom/shared-utils';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { PgTable, PgTableColumn, PgTableRow, PgChart, PgKpiCard } from '@pgcustom/design-system';
import type { ClientesApi } from './clientes.api.js';
import type { DashboardApi } from '../dashboard/dashboard.api.js';
import type { PermissoesClientes } from './clientes.permissoes.js';
import { rotuloClassificacao } from './classificacao.js';
import type { ErpClienteFormModal } from './cliente-form-modal.js';

const LIMITE_PAGINA = 20;
const ATRASO_DEBOUNCE_MS = 300;

const COLUNAS: PgTableColumn[] = [
  { key: 'nome', label: 'Nome' },
  { key: 'email', label: 'E-mail' },
  { key: 'telefone', label: 'Telefone' },
  { key: 'cpf', label: 'CPF' },
  { key: 'classificacao', label: 'Classificação' },
];

const TOM_CLASSIFICACAO: Record<ClassificacaoCliente, 'neutro' | 'destaque' | 'alerta'> = {
  padrao: 'neutro',
  recorrente: 'destaque',
  vip: 'alerta',
};

const COR_CLASSIFICACAO: Record<ClassificacaoCliente, string> = {
  padrao: 'var(--pg-color-texto-secundario)',
  recorrente: 'var(--pg-color-destaque)',
  vip: 'var(--pg-color-alerta)',
};

/**
 * `<erp-clientes-page>` — listagem/CRUD de Clientes (Sprint 3,
 * `ROADMAP.md`: "primeira tela real do ERP"). Composição de
 * `pg-table` (lista) + `pg-empty-state` (carregando/vazio/erro/sem-
 * permissão) + `erp-cliente-form-modal` (criar/editar) + `pg-modal`
 * (confirmação de exclusão), mesmo padrão de injeção de dependência via
 * propriedade JS de `erp-dashboard-page`/`erp-login-page`.
 *
 * Paginação por `limit`/`offset` (Anterior/Próxima) — a API não expõe
 * contagem total (`clientes.controller.ts`), então "há próxima página"
 * é inferido por a página atual ter vindo cheia (mesma convenção já
 * usada em `dashboard.api.ts` para o KPI de clientes).
 */
export class ErpClientesPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _clientesApi: ClientesApi | null = null;
  private _dashboardApi: DashboardApi | null = null;
  private _permissoes: PermissoesClientes = { ler: false, criar: false, atualizar: false, excluir: false };

  private busca = '';
  private classificacao: ClassificacaoCliente | '' = '';
  private offset = 0;
  private resultado: Resultado<Cliente[]> = { status: 'carregando' };
  private itensAtuais: Cliente[] = [];
  private clienteParaExcluir: Cliente | null = null;
  private excluindo = false;
  private erroExclusao = '';
  private debounceTimer: ReturnType<typeof setTimeout> | undefined;

  private modalFormulario!: ErpClienteFormModal;
  private modalExclusao!: HTMLElement;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set clientesApi(valor: ClientesApi) {
    this._clientesApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set dashboardApi(valor: DashboardApi) {
    this._dashboardApi = valor;
    if (this.isConnected) void this.carregarTotal();
  }

  set permissoes(valor: PermissoesClientes) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._clientesApi) void this.carregar();
    if (this._dashboardApi) void this.carregarTotal();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .topo {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-5, 24px);
          flex-wrap: wrap;
        }
        h1 {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-xl, 1.5rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin: 0;
        }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: 2px; }
        .filtros {
          display: flex;
          gap: var(--pg-space-3, 12px);
          margin-bottom: var(--pg-space-4, 16px);
          flex-wrap: wrap;
        }
        ${FORM_FIELD_CSS}
        #busca { flex: 1; min-width: 220px; }
        .paginacao {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--pg-space-4, 16px);
          margin-top: var(--pg-space-4, 16px);
        }
        .info-pagina { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-texto-secundario, #9aa1ab); }
        .erro-exclusao { font-size: var(--pg-font-size-xs, 0.75rem); color: var(--pg-color-erro, #e5484d); }
        .erro-exclusao:empty { display: none; }
        @media (max-width: 640px) {
          .topo { flex-direction: column; align-items: stretch; }
          .filtros { flex-direction: column; }
        }
        .layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(220px, 280px); gap: var(--pg-space-4, 16px); align-items: stretch; margin-bottom: var(--pg-space-5, 24px); }
        @media (max-width: 900px) { .layout { grid-template-columns: 1fr; } }
      </style>
      <div class="topo">
        <div>
          <h1>Clientes</h1>
          <div class="subtitulo">Cadastro de clientes — busque, filtre e edite classificações</div>
        </div>
        ${this._permissoes.criar ? '<pg-button id="botao-novo" variant="primary">Novo cliente</pg-button>' : ''}
      </div>

      <div class="layout">
        <pg-kpi-card id="kpi-total" label="Total de clientes" icone="usuarios" estado="carregando"></pg-kpi-card>
        <pg-card padding="compacto">
          <span slot="header">Classificação (página atual)</span>
          <div id="donut-classificacao"><pg-empty-state variant="carregando" titulo="Classificação"></pg-empty-state></div>
        </pg-card>
      </div>

      <div class="filtros">
        <input id="busca" type="search" placeholder="Buscar por nome, e-mail ou CPF" value="${escaparHtml(this.busca)}" />
        <select id="classificacao">
          <option value="">Todas as classificações</option>
          ${CLASSIFICACOES_HTML}
        </select>
      </div>
      <pg-card padding="compacto">
        <div id="conteudo"></div>
      </pg-card>
      <div class="paginacao">
        <pg-button id="anterior" variant="secondary">Anterior</pg-button>
        <span id="info-pagina" class="info-pagina"></span>
        <pg-button id="proxima" variant="secondary">Próxima</pg-button>
      </div>
      <erp-cliente-form-modal id="modal-formulario"></erp-cliente-form-modal>
      <pg-modal id="modal-exclusao">
        <span slot="header">Excluir cliente</span>
        <p id="texto-exclusao"></p>
        <span id="erro-exclusao" class="erro-exclusao"></span>
        <div slot="footer">
          <pg-button id="cancelar-exclusao" variant="ghost">Cancelar</pg-button>
          <pg-button id="confirmar-exclusao" variant="danger">Excluir</pg-button>
        </div>
      </pg-modal>
    `;

    this.modalFormulario = this.root.querySelector('#modal-formulario') as ErpClienteFormModal;
    if (this._clientesApi) this.modalFormulario.clientesApi = this._clientesApi;
    this.modalFormulario.permissoes = this._permissoes;
    this.modalFormulario.aoSalvar = () => void this.carregar();
    this.modalFormulario.addEventListener('erp-cliente-excluir-solicitado', (evento) => {
      const { cliente } = (evento as CustomEvent<{ cliente: Cliente }>).detail;
      this.abrirConfirmacaoExclusao(cliente);
    });

    this.modalExclusao = this.root.querySelector('#modal-exclusao') as HTMLElement;
    this.root.querySelector('#cancelar-exclusao')?.addEventListener('pg-click', () => this.fecharConfirmacaoExclusao());
    this.modalExclusao.addEventListener('pg-close', () => this.fecharConfirmacaoExclusao());
    this.root.querySelector('#confirmar-exclusao')?.addEventListener('pg-click', () => void this.confirmarExclusao());

    this.root.querySelector('#botao-novo')?.addEventListener('pg-click', () => this.modalFormulario.abrirParaCriar());

    const buscaEl = this.root.querySelector<HTMLInputElement>('#busca');
    buscaEl?.addEventListener('input', () => {
      const valor = buscaEl.value;
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.busca = valor.trim();
        this.offset = 0;
        void this.carregar();
      }, ATRASO_DEBOUNCE_MS);
    });

    const classificacaoEl = this.root.querySelector<HTMLSelectElement>('#classificacao');
    classificacaoEl!.value = this.classificacao;
    classificacaoEl?.addEventListener('change', () => {
      this.classificacao = classificacaoEl.value as ClassificacaoCliente | '';
      this.offset = 0;
      void this.carregar();
    });

    this.root.querySelector('#anterior')?.addEventListener('pg-click', () => {
      if (this.offset === 0) return;
      this.offset = Math.max(0, this.offset - LIMITE_PAGINA);
      void this.carregar();
    });
    this.root.querySelector('#proxima')?.addEventListener('pg-click', () => {
      this.offset += LIMITE_PAGINA;
      void this.carregar();
    });

    this.root.querySelector('#conteudo')?.addEventListener('pg-row-click', (evento) => {
      const { index } = (evento as CustomEvent<{ row: PgTableRow; index: number }>).detail;
      const cliente = this.itensAtuais[index];
      if (cliente) this.modalFormulario.abrirParaEditar(cliente);
    });

    this.atualizarConteudo();
  }

  private async carregarTotal(): Promise<void> {
    const api = this._dashboardApi;
    const kpi = this.root.querySelector('#kpi-total') as PgKpiCard | null;
    if (!api || !kpi) return;
    const resultado = await api.contarClientes();
    if (resultado.status === 'ok') {
      kpi.setAttribute('estado', 'ok');
      kpi.setAttribute('valor', String(resultado.dado.total));
      if (resultado.dado.aproximado) kpi.setAttribute('dica', `${resultado.dado.total}+ (primeira página)`);
      return;
    }
    if (resultado.status === 'vazio') {
      kpi.setAttribute('estado', 'ok');
      kpi.setAttribute('valor', '0');
      return;
    }
    kpi.setAttribute('estado', 'indisponivel');
  }

  private async carregar(): Promise<void> {
    const api = this._clientesApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultado = { status: 'sem-permissao' };
      this.itensAtuais = [];
      this.atualizarConteudo();
      return;
    }

    this.resultado = { status: 'carregando' };
    this.atualizarConteudo();

    const resultado = await api.listar({
      ...(this.busca ? { busca: this.busca } : {}),
      ...(this.classificacao ? { classificacao: this.classificacao } : {}),
      limit: LIMITE_PAGINA,
      offset: this.offset,
    });

    this.resultado = resultado;
    this.itensAtuais = resultado.status === 'ok' ? resultado.dado : [];
    this.atualizarConteudo();
  }

  private atualizarConteudo(): void {
    const conteudo = this.root.querySelector('#conteudo');
    if (!conteudo) return;

    const donut = this.root.querySelector('#donut-classificacao');

    if (this.resultado.status === 'ok') {
      conteudo.innerHTML = '<pg-table></pg-table>';
      const tabela = conteudo.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS;
      tabela.rows = this.itensAtuais.map(clienteParaLinha);

      if (donut) {
        const contagem: Record<ClassificacaoCliente, number> = { padrao: 0, recorrente: 0, vip: 0 };
        for (const c of this.itensAtuais) contagem[c.classificacao]++;
        donut.innerHTML = '<pg-chart></pg-chart>';
        const chart = donut.querySelector('pg-chart') as PgChart;
        chart.type = 'donut';
        chart.series = [{ key: 'valor', label: '' }];
        chart.points = (['padrao', 'recorrente', 'vip'] as ClassificacaoCliente[]).map((c) => ({
          label: rotuloClassificacao(c),
          values: { valor: contagem[c] },
          color: COR_CLASSIFICACAO[c],
        }));
        chart.formatarValor = (valor) => String(valor);
      }
    } else {
      const { variant, titulo, descricao } = estadoVazio(this.resultado);
      conteudo.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
      if (donut) donut.innerHTML = `<pg-empty-state variant="${variant}" titulo="Classificação" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    }

    const anterior = this.root.querySelector('#anterior');
    const proxima = this.root.querySelector('#proxima');
    const infoPagina = this.root.querySelector('#info-pagina');

    const paginaCheia = this.resultado.status === 'ok' && this.itensAtuais.length === LIMITE_PAGINA;
    if (this.offset === 0) anterior?.setAttribute('disabled', '');
    else anterior?.removeAttribute('disabled');
    if (paginaCheia) proxima?.removeAttribute('disabled');
    else proxima?.setAttribute('disabled', '');

    if (infoPagina) {
      infoPagina.textContent =
        this.resultado.status === 'ok'
          ? `${this.offset + 1}–${this.offset + this.itensAtuais.length}`
          : '';
    }
  }

  private abrirConfirmacaoExclusao(cliente: Cliente): void {
    this.clienteParaExcluir = cliente;
    this.erroExclusao = '';
    const texto = this.root.querySelector('#texto-exclusao');
    if (texto) texto.textContent = `Excluir "${cliente.nome}"? Esta ação não pode ser desfeita.`;
    const erro = this.root.querySelector('#erro-exclusao');
    if (erro) erro.textContent = '';
    this.modalExclusao.setAttribute('open', '');
  }

  private fecharConfirmacaoExclusao(): void {
    if (this.excluindo) return;
    this.clienteParaExcluir = null;
    this.modalExclusao.removeAttribute('open');
  }

  private async confirmarExclusao(): Promise<void> {
    const api = this._clientesApi;
    const cliente = this.clienteParaExcluir;
    if (!api || !cliente || this.excluindo) return;

    this.excluindo = true;
    const botaoConfirmar = this.root.querySelector('#confirmar-exclusao');
    botaoConfirmar?.setAttribute('loading', '');

    try {
      await api.excluir(cliente.id);
      this.modalExclusao.removeAttribute('open');
      this.clienteParaExcluir = null;
      // Excluir o único item de uma página além da primeira deixaria a
      // tela "vazia" num offset que já não tem mais nenhum registro —
      // volta uma página automaticamente nesse caso específico, em vez
      // de exigir que o usuário clique "Anterior" manualmente.
      if (this.itensAtuais.length === 1 && this.offset > 0) {
        this.offset = Math.max(0, this.offset - LIMITE_PAGINA);
      }
      void this.carregar();
    } catch (erro) {
      this.erroExclusao = erro instanceof ApiError ? erro.message : 'não foi possível excluir. tente novamente.';
      const erroEl = this.root.querySelector('#erro-exclusao');
      if (erroEl) erroEl.textContent = this.erroExclusao;
    } finally {
      this.excluindo = false;
      botaoConfirmar?.removeAttribute('loading');
    }
  }
}

function clienteParaLinha(cliente: Cliente): PgTableRow {
  return {
    nome: cliente.nome,
    email: cliente.email ?? '—',
    telefone: cliente.telefone ?? '—',
    cpf: cliente.cpf ? formatarCpfExibicao(cliente.cpf) : '—',
    classificacao: { texto: rotuloClassificacao(cliente.classificacao), tom: TOM_CLASSIFICACAO[cliente.classificacao] },
  };
}

function estadoVazio(resultado: Resultado<Cliente[]>): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo: 'Clientes', descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo: 'Clientes', descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo: 'Clientes', descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhum cliente encontrado', descricao: 'ajuste a busca/filtro ou cadastre um novo cliente' };
  }
}

const CLASSIFICACOES_HTML = ['padrao', 'recorrente', 'vip']
  .map((valor) => `<option value="${valor}">${rotuloClassificacao(valor as ClassificacaoCliente)}</option>`)
  .join('');

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-clientes-page', ErpClientesPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-clientes-page': ErpClientesPage;
  }
}
